import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, BookOpen, CheckCircle, Clock, Award } from 'lucide-react';
import { trainingApi, TrainingModule, TrainingAssignment, TrainingStats } from '../api/training.api';
import { useAuth } from '@contexts/AuthContext';
import PageInfoButton from '@/components/PageInfoButton';

const statusColors: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  failed: 'bg-orange-100 text-orange-800',
  exempted: 'bg-purple-100 text-purple-700',
};

const EMPTY_MODULE = {
  title: '',
  category: 'security_awareness',
  description: '',
  content: '',
  duration_minutes: 30,
  passing_score: 70,
  is_mandatory: false,
  is_active: true,
};

const CATEGORIES = [
  { value: 'security_awareness', label: 'Security Awareness' },
  { value: 'data_protection', label: 'Data Protection' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'ethics', label: 'Ethics' },
  { value: 'other', label: 'Other' },
];

const TrainingDashboard: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<'modules' | 'my' | 'all'>('my');
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [myAssignments, setMyAssignments] = useState<TrainingAssignment[]>([]);
  const [allAssignments, setAllAssignments] = useState<TrainingAssignment[]>([]);
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [createModal, setCreateModal] = useState(false);
  const [editModule, setEditModule] = useState<TrainingModule | null>(null);
  const [form, setForm] = useState({ ...EMPTY_MODULE });
  const [submitting, setSubmitting] = useState(false);

  const [quizModal, setQuizModal] = useState<TrainingAssignment | null>(null);
  const [quizStep, setQuizStep] = useState<'content' | 'quiz' | 'result'>('content');
  const [quizModule, setQuizModule] = useState<TrainingModule | null>(null);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [quizResult, setQuizResult] = useState<{ passed: boolean; score: number; passing_score: number } | null>(null);

  const canManage = hasPermission('compliance.update');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [modRes, myRes, statsRes] = await Promise.all([
        trainingApi.getModules(),
        trainingApi.getMyAssignments(),
        trainingApi.getStats(),
      ]);
      setModules(modRes.modules || []);
      setMyAssignments(myRes.assignments || []);
      setStats(statsRes);

      if (canManage) {
        const allRes = await trainingApi.getCompanyAssignments();
        setAllAssignments(allRes.assignments || []);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY_MODULE }); setEditModule(null); setCreateModal(true); };
  const openEdit = (m: TrainingModule) => {
    setForm({
      title: m.title, category: m.category, description: m.description || '',
      content: m.content || '', duration_minutes: m.duration_minutes,
      passing_score: m.passing_score, is_mandatory: m.is_mandatory, is_active: m.is_active,
    });
    setEditModule(m);
    setCreateModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.category) { alert('Title and category are required'); return; }
    setSubmitting(true);
    try {
      if (editModule) {
        await trainingApi.updateModule(editModule.id, form as any);
      } else {
        await trainingApi.createModule(form as any);
      }
      setCreateModal(false);
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this training module?')) return;
    try {
      await trainingApi.deleteModule(id);
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const startQuiz = async (assignment: TrainingAssignment) => {
    try {
      const mod = await trainingApi.getModule(assignment.module_id);
      setQuizModule(mod);
      setQuizModal(assignment);
      setQuizStep('content');
      setAnswers({});
      setQuizResult(null);
    } catch (e: any) {
      alert(e.message);
    }
  };

  const submitQuiz = async () => {
    if (!quizModal || !quizModule) return;
    setSubmitting(true);
    try {
      const numericAnswers: Record<number, number> = {};
      Object.entries(answers).forEach(([k, v]) => {
        numericAnswers[Number(k)] = Number(v);
      });
      const result = await trainingApi.submitCompletion(quizModal.id, { answers: numericAnswers });
      setQuizResult(result);
      setQuizStep('result');
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      <span className="ml-2 text-slate-600">Loading...</span>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Training & Awareness</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-1">Manage compliance training modules and track completions</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {canManage && (
            <button onClick={openCreate} className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> New Module
            </button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Modules', value: stats.total_modules, icon: <BookOpen className="w-5 h-5 text-blue-500" /> },
            { label: 'Completion Rate', value: `${stats.completion_rate}%`, icon: <CheckCircle className="w-5 h-5 text-green-500" /> },
            { label: 'Overdue', value: stats.overdue, icon: <Clock className="w-5 h-5 text-red-500" /> },
            { label: 'Avg Score', value: `${stats.average_score}%`, icon: <Award className="w-5 h-5 text-yellow-500" /> },
          ].map(s => (
            <div key={s.label} className="bg-white border rounded-xl p-4 flex items-center gap-3">
              {s.icon}
              <div>
                <div className="text-xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b">
        {[
          { key: 'my', label: 'My Assignments' },
          ...(canManage ? [{ key: 'modules', label: 'Manage Modules' }, { key: 'all', label: 'All Assignments' }] : []),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* My Assignments Tab */}
      {activeTab === 'my' && (
        <div className="space-y-3">
          {myAssignments.length === 0 ? (
            <div className="py-12 text-slate-400">No training assignments yet.</div>
          ) : (
            myAssignments.map(a => (
              <div key={a.id} className="bg-white border rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">{a.module_title}</div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    {a.module_category.replace('_', ' ')} · {a.module_duration} min
                    {a.due_date && ` · Due ${new Date(a.due_date).toLocaleDateString()}`}
                  </div>
                  {a.score != null && (
                    <div className="text-xs text-slate-400 mt-0.5">Score: {a.score}%</div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[a.status] || 'bg-gray-100 text-gray-700'}`}>
                    {a.status.replace('_', ' ')}
                  </span>
                  {a.status !== 'completed' && a.status !== 'exempted' && (
                    <button
                      onClick={() => startQuiz(a)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {a.status === 'failed' ? 'Retake' : 'Start'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modules Tab */}
      {activeTab === 'modules' && canManage && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['Title', 'Category', 'Duration', 'Mandatory', 'Active', 'Completion', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {modules.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No modules yet.</td></tr>
              ) : modules.map(m => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-left font-medium text-slate-900">{m.title}</td>
                  <td className="px-4 py-3 text-left text-slate-600 capitalize">{m.category.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-left text-slate-600">{m.duration_minutes} min</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`text-xs px-2 py-1 rounded-full ${m.is_mandatory ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {m.is_mandatory ? 'Mandatory' : 'Optional'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left">
                    <span className={`text-xs px-2 py-1 rounded-full ${m.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left text-slate-600">
                    {m.completion_rate != null ? `${m.completion_rate}%` : '—'}
                  </td>
                  <td className="px-4 py-3 text-left">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(m)} className="text-xs px-2 py-1 border rounded hover:bg-slate-50 text-slate-600">Edit</button>
                      <button onClick={() => handleDelete(m.id)} className="text-xs px-2 py-1 border rounded hover:bg-red-50 text-red-600">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* All Assignments Tab */}
      {activeTab === 'all' && canManage && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['User', 'Module', 'Status', 'Score', 'Due Date', 'Completed'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {allAssignments.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No assignments yet.</td></tr>
              ) : allAssignments.map(a => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-left text-slate-900">{(a as any).user_name || `User ${a.user_id}`}</td>
                  <td className="px-4 py-3 text-left text-slate-600">{a.module_title}</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[a.status]}`}>
                      {a.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left text-slate-600">{a.score != null ? `${a.score}%` : '—'}</td>
                  <td className="px-4 py-3 text-left text-slate-500">{a.due_date ? new Date(a.due_date).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-left text-slate-500">{a.completed_at ? new Date(a.completed_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Module Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-900">{editModule ? 'Edit Module' : 'New Training Module'}</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content / Material</label>
                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={4} placeholder="Paste training content or instructions here..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Duration (minutes)</label>
                  <input type="number" min={1} value={form.duration_minutes}
                    onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Passing Score (%)</label>
                  <input type="number" min={0} max={100} value={form.passing_score}
                    onChange={e => setForm(f => ({ ...f, passing_score: Number(e.target.value) }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_mandatory} onChange={e => setForm(f => ({ ...f, is_mandatory: e.target.checked }))} />
                  <span>Mandatory</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <span>Active</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleSave} disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : editModule ? 'Save Changes' : 'Create Module'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {quizModal && quizModule && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-900">{quizModule.title}</h2>
              <p className="text-sm text-slate-500">{quizModule.category.replace('_', ' ')} · {quizModule.duration_minutes} min · Pass: {quizModule.passing_score}%</p>
            </div>

            {quizStep === 'content' && (
              <div className="px-6 py-4 space-y-4">
                {quizModule.content ? (
                  <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{quizModule.content}</div>
                ) : (
                  <p className="text-slate-500 italic">No content provided for this module.</p>
                )}
                <div className="pt-4 flex justify-end gap-3">
                  <button onClick={() => setQuizModal(null)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Close</button>
                  {quizModule.questions && quizModule.questions.length > 0 && (
                    <button onClick={() => setQuizStep('quiz')} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Take Quiz
                    </button>
                  )}
                </div>
              </div>
            )}

            {quizStep === 'quiz' && quizModule.questions && (
              <div className="px-6 py-4 space-y-6">
                {quizModule.questions.map((q, i) => (
                  <div key={q.id} className="space-y-2">
                    <p className="font-medium text-slate-800">{i + 1}. {q.question}</p>
                    {['a', 'b', 'c', 'd'].map(opt => (
                      <label key={opt} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        answers[q.id] === opt ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                      }`}>
                        <input type="radio" name={`q-${q.id}`} value={opt}
                          checked={answers[q.id] === opt}
                          onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))}
                          className="mt-0.5" />
                        <span className="text-sm text-slate-700">{(q as any)[`option_${opt}`]}</span>
                      </label>
                    ))}
                  </div>
                ))}
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setQuizStep('content')} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Back</button>
                  <button onClick={submitQuiz} disabled={submitting}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {submitting ? 'Submitting...' : 'Submit Answers'}
                  </button>
                </div>
              </div>
            )}

            {quizStep === 'result' && quizResult && (
              <div className="px-6 py-8 text-center space-y-4">
                <div className={`text-6xl font-bold ${quizResult.passed ? 'text-green-600' : 'text-red-500'}`}>
                  {quizResult.score}%
                </div>
                <div className={`text-xl font-semibold ${quizResult.passed ? 'text-green-700' : 'text-red-600'}`}>
                  {quizResult.passed ? '🎉 Passed!' : 'Not Passed'}
                </div>
                <p className="text-slate-500 text-sm">Passing score: {quizResult.passing_score}%</p>
                <button onClick={() => setQuizModal(null)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDashboard;
